import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Toolbar, ToolbarHeading, ToolbarPageTitle } from '@/partials/toolbar';
import { Container, BackButton, Button, Shimmer } from '@/components';
import {
  useGetDeploymentByIdQuery,
  useConfirmDeploymentMutation,
} from '@/services/DeploymentService';
import { handleError } from '@/utils/BaseQuery';
import { ConfirmationDialog } from '@/components/dialogBox';
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Hash,
  Paperclip,
  User,
} from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { InterfaceType, ManualDeploymentStatus } from '@/constant';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDateTime } from '@/utils/Date';

const DeploymentDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();

  const { data, isLoading, isError, refetch } = useGetDeploymentByIdQuery(
    id as string,
    { skip: !id }
  );

  const deploymentDetails = data?.data;
  const [confirmDeployment, { isLoading: isConfirming }] =
    useConfirmDeploymentMutation();

  const isUpdating = isConfirming;

  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    deploymentId: string | null;
    action: 'confirm' | 'decline' | null;
    release_notes?: string;
    attachment?: File | null;
    release_notes_url?: string;
  }>({ isOpen: false, deploymentId: null, action: null, attachment: null });

  useEffect(() => {
    if (
      deploymentDetails?.metadata?.status ===
        ManualDeploymentStatus.CONFIRMED ||
      deploymentDetails?.metadata?.status === ManualDeploymentStatus.FAILED
    ) {
      setConfirmActionState({
        isOpen: false,
        deploymentId: null,
        action: null,
      });
    }
  }, [deploymentDetails?.metadata?.status]);

  const handleActionClick = (
    deploymentId: string,
    action: 'confirm' | 'decline'
  ) => {
    setConfirmActionState({
      isOpen: true,
      deploymentId,
      action,
      release_notes: '',
      attachment: null,
      release_notes_url: '',
    });
  };

  const getInterfaceLabel = (type?: number) => {
    if (type === InterfaceType.ADMIN) return 'Admin';
    if (type === InterfaceType.FRONTEND) return 'Frontend';
    if (type === InterfaceType.BACKEND) return 'Backend';
    return '-';
  };

  const getFilenameFromUrl = (url?: string | null) => {
    if (!url) return 'Attachment';
    try {
      const parts = url.split('?')[0].split('/');
      return parts[parts.length - 1] || 'Attachment';
    } catch {
      return 'Attachment';
    }
  };

  const handleConfirmAction = async () => {
    const {
      deploymentId,
      action,
      release_notes,
      attachment,
      release_notes_url,
    } = confirmActionState;
    if (!deploymentId || !action) return;

    try {
      const status = action === 'confirm' ? 1 : 2; // ManualDeploymentStatus.CONFIRMED or FAILED
      await confirmDeployment({
        id: deploymentId,
        update: {
          release_notes: release_notes || undefined,
          status,
          attachment: attachment || undefined,
          release_notes_url: release_notes_url || undefined,
        },
      }).unwrap();
      enqueueSnackbar(
        action === 'confirm'
          ? 'Deployment confirmed successfully!'
          : 'Deployment declined successfully!',
        { variant: 'success' }
      );
      refetch();
      setConfirmActionState({
        isOpen: false,
        deploymentId: null,
        action: null,
        release_notes: undefined,
        attachment: null,
        release_notes_url: undefined,
      });
    } catch (error) {
      handleError(error as Error);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setConfirmActionState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setConfirmActionState((prev) => ({ ...prev, attachment: file }));
    }
  };

  const getStatusDisplay = (
    deployment:
      | import('@/types/deployement-management.types').Deployment
      | undefined
  ) => {
    const status = deployment?.metadata?.status;

    if (status === ManualDeploymentStatus.CONFIRMED) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200">
          <CheckCircle className="w-4 h-4" />
          Confirmed
        </div>
      );
    }

    if (status === ManualDeploymentStatus.FAILED) {
      return (
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-red-50 text-red-700 border border-red-200">
          <AlertCircle className="w-4 h-4" />
          Declined
        </div>
      );
    }

    return (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-yellow-50 dark:bg-yellow-800 contrast:bg-yellow-800 text-yellow-700 dark:text-yellow-50 contrast:text-yellow-50 border border-yellow-200 dark:border-yellow-800 contrast:border-yellow-800">
        <Clock className="w-4 h-4" />
        Pending Review
      </div>
    );
  };

  const renderLinkOrDash = (url: string | null, label: string) => {
    if (!url) return <span className="text-gray-500">-</span>;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium"
      >
        <ExternalLink className="w-3 h-3" />
        {label}
      </a>
    );
  };

  const backPath = '/deployements';

  return (
    <Container>
      {/* Toolbar */}
      <Toolbar>
        <ToolbarHeading>
          <div className="flex gap-2 items-center">
            <BackButton customBackPath={backPath} />
            <ToolbarPageTitle text="Deployment Details" />
          </div>
        </ToolbarHeading>

        <div className="flex gap-2.5 justify-center items-center mt-4 sm:mt-0">
          {deploymentDetails &&
            deploymentDetails.metadata?.status !==
              ManualDeploymentStatus.CONFIRMED &&
            deploymentDetails.metadata?.status !==
              ManualDeploymentStatus.FAILED &&
            hasPermission('deployment_management_update') && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="primary"
                  onClick={() =>
                    handleActionClick(deploymentDetails.id, 'confirm')
                  }
                  disabled={isUpdating}
                >
                  Confirm Deployment
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() =>
                    handleActionClick(deploymentDetails.id, 'decline')
                  }
                  disabled={isUpdating}
                >
                  Decline Deployment
                </Button>
              </div>
            )}
        </div>
      </Toolbar>

      {/* Main Details Card */}
      <div className="card mx-auto mb-[30px]">
        {isLoading ? (
          <Shimmer height="300px" borderRadius="8px" />
        ) : isError ? (
          <div className="p-5 text-red-600 text-center" role="alert">
            <AlertCircle className="w-8 h-8 mx-auto mb-2" />
            <p>Error loading deployment details. Please try again.</p>
          </div>
        ) : (
          <section className="p-5">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Version */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    Version
                  </span>
                </div>
                <span className="font-medium text-gray-900">
                  {deploymentDetails?.version}
                </span>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    Status
                  </span>
                </div>
                <div className="flex items-center">
                  {getStatusDisplay(deploymentDetails)}
                </div>
              </div>

              {/* Deployed At */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">
                    Deployed At
                  </span>
                </div>
                <span className="text-gray-900">
                  {formatDisplayDateTime(deploymentDetails?.deployed_at || '')}
                </span>
              </div>

              {/* Confirmed At */}
              {deploymentDetails?.confirmed_at && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">
                      Confirmed At
                    </span>
                  </div>
                  <span className="text-gray-900">
                    {formatDisplayDateTime(deploymentDetails.confirmed_at)}
                  </span>
                </div>
              )}

              {/* Declined At */}
              {deploymentDetails?.declined_at && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">
                      Declined At
                    </span>
                  </div>
                  <span className="text-gray-900">
                    {formatDisplayDateTime(deploymentDetails.declined_at)}
                  </span>
                </div>
              )}

              {/* Confirmed By */}
              {deploymentDetails?.confirmed_by && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">
                      Confirmed By
                    </span>
                  </div>
                  <span className="text-gray-900">
                    {deploymentDetails.confirmed_by}
                  </span>
                </div>
              )}

              {/* Interface Type */}
              {deploymentDetails?.interface_type && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-500">
                      Interface Type
                    </span>
                  </div>
                  <span className="font-medium text-gray-900">
                    {getInterfaceLabel(
                      deploymentDetails.interface_type as unknown as number
                    )}
                  </span>
                </div>
              )}
            </div>

            {/* Release Notes Section */}
            {(deploymentDetails?.release_notes ||
              deploymentDetails?.release_notes_url) && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-400" />
                  Release Notes
                </h3>

                {deploymentDetails.release_notes && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {deploymentDetails.release_notes}
                    </p>
                  </div>
                )}

                {deploymentDetails.release_notes_url && (
                  <div className="flex items-center gap-3">
                    {renderLinkOrDash(
                      deploymentDetails.release_notes_url,
                      'View Release Notes'
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Attachment Section */}
            {deploymentDetails?.attachment && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Paperclip className="w-5 h-5 text-gray-400" />
                  Attachment
                </h3>
                <div className="flex items-center gap-3">
                  <a
                    href={deploymentDetails.attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-200 text-blue-700 hover:text-blue-800 hover:bg-blue-50 text-sm font-medium"
                    aria-label={`Download attachment ${getFilenameFromUrl(deploymentDetails.attachment)}`}
                  >
                    <Paperclip className="w-4 h-4" />
                    {getFilenameFromUrl(deploymentDetails.attachment)}
                  </a>
                </div>
              </div>
            )}

            {/* Decline Reason */}
            {deploymentDetails?.declined_reason && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-gray-400" />
                  Decline Reason
                </h3>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-red-700">
                    {deploymentDetails.declined_reason}
                  </p>
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationDialog
        open={confirmActionState.isOpen}
        onClose={() =>
          setConfirmActionState({
            isOpen: false,
            deploymentId: null,
            action: null,
            release_notes: undefined,
            attachment: null,
            release_notes_url: undefined,
          })
        }
        onConfirm={handleConfirmAction}
        message={
          confirmActionState.action === 'confirm' ? (
            <div className="space-y-4">
              <p className="text-gray-700">
                Confirm that this deployment was successful and ready for
                production use.
              </p>
              <div className="space-y-3">
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-1"
                    htmlFor="release-notes"
                  >
                    Release Notes
                  </label>
                  <textarea
                    id="release-notes"
                    name="release_notes"
                    placeholder="Describe the changes and improvements in this deployment..."
                    value={confirmActionState.release_notes || ''}
                    onChange={handleInputChange}
                    rows={4}
                    autoComplete="off"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                    disabled={isUpdating}
                  />
                </div>
                <div>
                  <label
                    htmlFor="release_notes_file"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Release Notes File
                  </label>
                  <input
                    id="release_notes_file"
                    type="file"
                    name="release_notes_file"
                    accept=".pdf,.doc,.docx,.txt,.md"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    disabled={isUpdating}
                  />
                </div>
                <div>
                  <label
                    htmlFor="release_notes_url"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Release Notes URL
                  </label>
                  <input
                    id="release_notes_url"
                    type="url"
                    name="release_notes_url"
                    placeholder="https://your-changelog.com/release/v1.2.3"
                    value={confirmActionState.release_notes_url || ''}
                    onChange={handleInputChange}
                    autoComplete="off"
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isUpdating}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-700">
                Are you sure you want to decline this deployment?
              </p>
            </div>
          )
        }
        cancelButtonText="Cancel"
        confirmButtonText={
          confirmActionState.action === 'confirm'
            ? 'Confirm Deployment'
            : 'Decline Deployment'
        }
      />
    </Container>
  );
};

export default DeploymentDetails;
